import { ColDef } from 'ag-grid-enterprise';

import { ObjectKeys, isNonNullable } from '@camelot/utils';
import { isStrictISODateString } from '@camelot/utils';

import { SelectionCheckboxComponent } from '../components/ag-grid/selection/selection-checkbox.component';
import { SelectionHeaderComponent } from '../components/ag-grid/selection/selection-header.component';
import { DateTimeKind, GridMetaData, ParameterType } from '../services/dto/metadata';
import { AsyncEnumCol } from './columns/async-enum/async-enum-col';
import { BaseCol, IColOptions } from './columns/base-col';
import { BoolCol } from './columns/bool/bool-col';
import { CurrencyCol } from './columns/currency/currency-col';
import { DateCol } from './columns/date/date-col';
import { EnumCol } from './columns/enum/enum-col';
import { GuidCol } from './columns/guid/guid-col';
import { NumberCol } from './columns/number/number-col';
import { PercentageCol } from './columns/percentage/percentage-col';
import { StringCol } from './columns/string/string-col';
import { TimeCol } from './columns/time/time-col';
import { CamGridData } from './grid-data';
import { IFieldsOverrides } from './types';

/** Colonne technique de cases à cocher ajoutée en sélection multiple : ce n'est pas un champ métier. */
export const GRID_SELECTION_COL_ID = 'selection';

export class CamGridColumns<T extends { id: string }> {
  constructor(private _grid: CamGridData<T>) {}

  public createColsModel(cols: GridMetaData[], fieldsOverrides: IFieldsOverrides, colOptions: IColOptions) {
    cols
      .map(field =>
        this._factoryCols(field, {
          ...colOptions,
          ...(fieldsOverrides[field.name] ? fieldsOverrides[field.name].colOptions : {}),
        })
      )
      .forEach(field => (this._grid.cols[field.key] = field));
  }

  public generateColsMetaData(data: T[], fieldsOverrides: IFieldsOverrides): GridMetaData[] {
    return [
      ...data
        .map(item => ObjectKeys(item))
        .reduce((set, current) => {
          current.forEach(item => set.add(<string>item));
          return set;
        }, new Set<string>()),
    ]
      .filter(item => !item.startsWith('_') && item !== 'hierarchyLabel')
      .map(item => this._getTypeOfField(data, <keyof T>item, fieldsOverrides[item]?.colOptions ?? {}));
  }

  public createFields(
    fieldsOverrides?: { [index: string]: ColDef },
    addcheckbox?: boolean,
    showHierarchyLabel = true
  ): ColDef[] {
    return [
      ...(addcheckbox === true
        ? [
            {
              cellRenderer: SelectionCheckboxComponent,
              colId: GRID_SELECTION_COL_ID,
              headerComponent: SelectionHeaderComponent,
              hide: false,
              lockPosition: true,
              resizable: false,
              sortable: false,
              width: 50,
            },
          ]
        : []),
      ...(this._grid.isTreeData && showHierarchyLabel
        ? [
            {
              editable: false,
              field: 'hierarchyLabel',
              headerName: 'grid.common.hierarchyLabel',
              lockPosition: true,
              resizable: false,
              sortable: false,
              width: 80,
            },
          ]
        : []),
      ...Object.keys(this._grid.cols)
        .filter(key => !key.startsWith('_'))
        .map(key => {
          const override = fieldsOverrides ? fieldsOverrides[key] || {} : {};
          return { ...this._grid.cols[key].getColDef(), ...override };
        }),
    ];
  }

  private _factoryCols(col: GridMetaData, colOptions: IColOptions): BaseCol<any> {
    if (this._grid.filters.forcedFilter && this._grid.filters.forcedFilter[col.name]) {
      colOptions.filters = { allow: false };
    }
    switch (col.type) {
      case ParameterType.String:
        return new StringCol({ col: col, options: colOptions, scope: this._grid.scope }, this._grid);
      case ParameterType.Enum:
        return new EnumCol({ col: col, options: colOptions, scope: this._grid.scope }, this._grid);
      case ParameterType.AsyncEnum:
        return new AsyncEnumCol({ col: col, options: colOptions, scope: this._grid.scope }, this._grid);
      case ParameterType.Number:
        return new NumberCol({ col: col, options: colOptions, scope: this._grid.scope }, this._grid);
      case ParameterType.Currency:
        return new CurrencyCol({ col: col, options: colOptions, scope: this._grid.scope }, this._grid);
      case ParameterType.Percentage:
        return new PercentageCol({ col: col, options: colOptions, scope: this._grid.scope }, this._grid);
      case ParameterType.Guid:
        return new GuidCol({ col: col, options: colOptions, scope: this._grid.scope }, this._grid);
      case ParameterType.DateTime:
        return new DateCol({ col: col, options: colOptions, scope: this._grid.scope }, this._grid);
      case ParameterType.Boolean:
        return new BoolCol({ col: col, options: colOptions, scope: this._grid.scope }, this._grid);
      case ParameterType.TimeStamp:
        return new TimeCol({ col: col, options: colOptions, scope: this._grid.scope }, this._grid);
      default:
        return new BaseCol({ col: col, options: colOptions, scope: this._grid.scope }, this._grid);
    }
  }

  private _getTypeOfField(data: T[], field: keyof T, colOptions: IColOptions): GridMetaData {
    const value = data.map(entity => entity[field]).filter(isNonNullable)[0];
    if (typeof value === 'string' && isStrictISODateString(value)) {
      return {
        additionalInfo: [],
        dateTimeKind: DateTimeKind.Date,
        meSearchEnabled: false,
        multiSearchEnabled: false,
        name: field.toString(),
        nullable: false,
        showEntireListWhenFiltering: false,
        shownByDefault: true,
        type: ParameterType.DateTime,
      };
    }
    switch (typeof value) {
      case 'boolean':
        return {
          additionalInfo: [],
          meSearchEnabled: false,
          multiSearchEnabled: false,
          name: field.toString(),
          nullable: false,
          showEntireListWhenFiltering: false,
          shownByDefault: true,
          type: ParameterType.Boolean,
        };
      case 'string':
        return {
          additionalInfo: [],
          meSearchEnabled: false,
          multiSearchEnabled: false,
          name: field.toString(),
          nullable: false,
          showEntireListWhenFiltering: false,
          shownByDefault: true,
          type: ParameterType.String,
        };
      case 'number': {
        if (colOptions.enumInfo) {
          return {
            additionalInfo: [],
            meSearchEnabled: false,
            multiSearchEnabled: false,
            name: field.toString(),
            nullable: false,
            showEntireListWhenFiltering: false,
            shownByDefault: true,
            type: ParameterType.Enum,
          };
        }
        return {
          additionalInfo: [],
          meSearchEnabled: false,
          multiSearchEnabled: false,
          name: field.toString(),
          nullable: false,
          showEntireListWhenFiltering: false,
          shownByDefault: true,
          type: ParameterType.Number,
        };
      }
      default:
        return {
          additionalInfo: [],
          meSearchEnabled: false,
          multiSearchEnabled: false,
          name: field.toString(),
          nullable: false,
          showEntireListWhenFiltering: false,
          shownByDefault: true,
          type: ParameterType.String,
        };
    }
  }
}
